"use client"

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react"
import * as Dialog from "@radix-ui/react-dialog"
import { ArrowLeft, ExternalLink, X } from "lucide-react"
import { PANEL_TOPICS, type PanelTopic } from "@/lib/breach/panel-topics"
import { DIRECTORIES } from "@/lib/breach/directory/refs"
import { INSURERS_VERIFIED_ON } from "@/lib/breach/insurers"
import { Disclosure } from "./directory-ui"
import { FindLawyerContent } from "./find-lawyer-content"
import { InsuranceDirectory } from "./insurance-directory"
import { IndividualsDirectory } from "./individuals-directory"
import { IrGuidance } from "./ir-guidance"
import { PlatformDirectory } from "./platform-directory"
import { ReportDirectory } from "./report-directory"
import { NewTabLink } from "./new-tab-link"

interface PanelState { topic: PanelTopic; anchor?: string }
interface PanelApi { open: (topic: PanelTopic, anchor?: string) => void }

const Ctx = createContext<PanelApi | null>(null)

/** Null outside an <InfoPanelProvider> (for example on public pages), so links can fall back to a new tab. */
export const useInfoPanel = () => useContext(Ctx)

const VERIFIED = "2026-11-03"

function Intro({ children }: { children: ReactNode }) {
  return <p className="mb-4 text-sm text-muted-foreground">{children}</p>
}

function Content({ state, open }: { state: PanelState; open: PanelApi["open"] }) {
  switch (state.topic) {
    case "hub":
      return (
        <div>
          <Intro>Directories to use alongside your plan. Pick one; you&apos;ll stay on this page.</Intro>
          <ul className="grid gap-3">
            {DIRECTORIES.map((d) => {
              const topic = (Object.entries(PANEL_TOPICS).find(([, v]) => v.fullHref === d.href)?.[0] ?? null) as PanelTopic | null
              return (
                <li key={d.id} className="rounded-lg border border-border p-4">
                  {topic ? <button type="button" className="text-left font-semibold underline" onClick={() => open(topic)}>{d.title}</button> : <span className="font-semibold">{d.title}</span>}
                  <p className="mt-1 text-sm text-muted-foreground">{d.blurb}</p>
                </li>
              )
            })}
          </ul>
        </div>
      )
    case "lawyer":
      return (
        <div>
          <Intro>A lawyer who handles data breaches can tell you which notification rules apply and when. Call one early, before you contact regulators or customers.</Intro>
          <FindLawyerContent panel />
        </div>
      )
    case "insurance":
      return (
        <div>
          <Intro>If you have cyber insurance, call it early: the policy may set notice requirements and may list breach lawyers and forensic firms the insurer approves.</Intro>
          <InsuranceDirectory />
          <Disclosure verifiedOn={INSURERS_VERIFIED_ON} subject="insurer or broker" />
        </div>
      )
    case "report":
      return (
        <div>
          <Intro>Reporting duties depend on your facts, your industry, and your contracts. Confirm with your lawyer what applies to you.</Intro>
          <ReportDirectory anchor={state.anchor} />
          <Disclosure verifiedOn={VERIFIED} subject="agency or organization" />
        </div>
      )
    case "platforms":
      return (
        <div>
          <Intro>Start with the platform&apos;s own pages. Export logs early: platforms keep them for limited periods that depend on your plan.</Intro>
          <PlatformDirectory anchor={state.anchor} />
          <Disclosure verifiedOn={VERIFIED} subject="platform" />
        </div>
      )
    case "ir-guidance":
      return (
        <div>
          <Intro>We don&apos;t list or recommend incident-response firms. InventiveHQ, which publishes this site, is one provider of such services, so we point you to neutral sources.</Intro>
          <IrGuidance />
          <Disclosure verifiedOn={VERIFIED} />
        </div>
      )
    case "individuals":
      return (
        <div>
          <Intro>Official and nonprofit resources for people whose information was exposed, and a place a business can point the people it notifies.</Intro>
          <IndividualsDirectory />
          <Disclosure verifiedOn={VERIFIED} />
        </div>
      )
    default:
      return <Intro>This guide is on its way. Open the full page for the latest.</Intro>
  }
}

export function InfoPanelProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PanelState | null>(null)
  const open = useCallback((topic: PanelTopic, anchor?: string) => setState({ topic, anchor }), [])
  const api = useMemo<PanelApi>(() => ({ open }), [open])
  const meta = state ? PANEL_TOPICS[state.topic] : null

  return (
    <Ctx.Provider value={api}>
      {children}
      <Dialog.Root open={!!state} onOpenChange={(o) => { if (!o) setState(null) }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 print:hidden" />
          <Dialog.Content
            className="fixed right-0 top-0 z-50 flex h-full w-full flex-col border-l border-border bg-background shadow-xl outline-none sm:max-w-2xl print:hidden"
            aria-describedby={undefined}
          >
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
              <div className="flex min-w-0 items-center gap-2">
                {state && state.topic !== "hub" && (
                  <button type="button" className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground" onClick={() => open("hub")} aria-label="Back to all resources">
                    <ArrowLeft className="h-5 w-5" aria-hidden />
                  </button>
                )}
                <Dialog.Title className="truncate text-lg font-bold">{meta?.title}</Dialog.Title>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {meta && (
                  <NewTabLink href={meta.fullHref} className="hidden items-center gap-1 text-sm text-muted-foreground underline hover:text-foreground sm:inline-flex">
                    Open full page <ExternalLink className="h-3 w-3" aria-hidden />
                  </NewTabLink>
                )}
                <Dialog.Close className="rounded p-1 text-muted-foreground hover:text-foreground" aria-label="Close and return to your plan">
                  <X className="h-5 w-5" aria-hidden />
                </Dialog.Close>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {state && <Content key={`${state.topic}:${state.anchor ?? ""}`} state={state} open={open} />}
            </div>
            <div className="border-t border-border px-4 py-3 text-xs text-muted-foreground">
              Your plan is right behind this panel. Close it to go back to exactly where you were.
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Ctx.Provider>
  )
}
